import {
  CmsComponentGrid,
  ContentComponentLibraryProvider,
} from '@johnlewispartnership/wtr-content-component-library';
import type { CmsComponentData } from '@johnlewispartnership/wtr-content-component-library';
import type { Config } from '@johnlewispartnership/wtr-content-component-library/dist/component-library/config/types';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';

import { logger } from 'logger';
import { BESPOKE_CONTENT_FRAGMENT_PATHS } from 'server/paths';

import AnalyticsSetup from './components/AnalyticsSetup';
import cmsComponentMap from './componentMap';
// For now, we always import the global grid styles
import './styles/grid.scss';
import siteWideBannerStyles from './styles/sitewide-banner.module.scss';

type FragmentAppProps = {
  componentData: CmsComponentData[];
  /**
   * componentLibraryConfig: for environment based config options that need to come from server
   */
  componentLibraryConfig: Omit<Config, 'componentMap'>;
  contentPath: string;
};

const FragmentAppContent = ({
  componentData,
  componentLibraryConfig,
  contentPath,
}: FragmentAppProps) => {
  const { trackEvent } = useAnalytics();

  const isSitewideBanner = contentPath === BESPOKE_CONTENT_FRAGMENT_PATHS['/sitewide-banner'].path;
  const position = isSitewideBanner ? 'sitewide-banner' : 'root';

  /**
   * For hardcoded isomorphic config options
   */
  const config: Config = {
    componentMap: cmsComponentMap,
    imageQuality: 'highQuality85',
    onAnalyticsEvent: trackEvent,
    headFunction: meta => meta,
    onError: logger.error,
    ...componentLibraryConfig,
  };

  const contentComponents = (
    <ContentComponentLibraryProvider config={config}>
      <CmsComponentGrid childComponents={componentData} position={position} preloadImages />
    </ContentComponentLibraryProvider>
  );

  if (isSitewideBanner) {
    return (
      <div
        role="region"
        aria-label="promotional banner"
        className={siteWideBannerStyles.container}
        data-testid="site-banner"
        suppressHydrationWarning
      >
        {contentComponents}
      </div>
    );
  }

  return contentComponents;
};

const FragmentApp = ({ componentData, componentLibraryConfig, contentPath }: FragmentAppProps) => (
  <AnalyticsSetup>
    <FragmentAppContent
      componentData={componentData}
      componentLibraryConfig={componentLibraryConfig}
      contentPath={contentPath}
    />
  </AnalyticsSetup>
);

export default FragmentApp;
